import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, paginated } from './common.js';
import { UserRefSchema } from './user.js';

/**
 * In-app-уведомления и наблюдатели задачи (UC-ISS-10, docs/05 §9).
 */

export const NotificationTypeSchema = z.enum([
  'issue.created',
  'issue.updated',
  'issue.transitioned',
  'issue.assigned',
  'comment.created',
]);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/**
 * Содержимое `notifications.payload` (jsonb): ссылка на задачу, актор и
 * детали изменения. Плоская форма — совпадает с доменным
 * `NotificationPayload` (`@tasks/domain`).
 */
export const NotificationPayloadSchema = z.object({
  issueId: UuidSchema,
  issueKey: z.string(),
  issueSummary: z.string(),
  actorId: UuidSchema.nullable(),
  actorName: z.string().nullable(),
  fields: z.array(z.string()),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  commentId: UuidSchema.nullable(),
});
export type NotificationPayload = z.infer<typeof NotificationPayloadSchema>;

export const NotificationSchema = z.object({
  id: UuidSchema,
  type: NotificationTypeSchema,
  payload: NotificationPayloadSchema,
  /** Ссылка на задачу дублируется на верхнем уровне для списка. */
  issueId: UuidSchema,
  issueKey: z.string(),
  /** Готовый текст на русском: считает домен по типу и payload. */
  text: z.string(),
  readAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationListSchema = paginated(NotificationSchema);

export const UnreadCountSchema = z.object({ count: z.number().int().nonnegative() });
export type UnreadCount = z.infer<typeof UnreadCountSchema>;

/** Отметить прочитанными: конкретные id или сразу все. */
export const MarkNotificationsReadBodySchema = z.union([
  z.object({ ids: z.array(UuidSchema).min(1).max(100) }).strict(),
  z.object({ all: z.literal(true) }).strict(),
]);
export type MarkNotificationsReadBody = z.infer<typeof MarkNotificationsReadBodySchema>;

export const MarkNotificationsReadResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
  count: z.number().int().nonnegative(),
});
export type MarkNotificationsReadResponse = z.infer<typeof MarkNotificationsReadResponseSchema>;

/** Наблюдатель задачи: пользователь + когда подписался. */
export const WatcherSchema = z.object({
  user: UserRefSchema,
  createdAt: IsoDateTimeSchema,
});
export type Watcher = z.infer<typeof WatcherSchema>;

export const WatcherListSchema = paginated(WatcherSchema).extend({
  /** Следит ли текущий пользователь. */
  isWatching: z.boolean(),
});
export type WatcherList = z.infer<typeof WatcherListSchema>;

/** Состояние подписки после watch/unwatch. */
export const WatchStateSchema = z.object({
  isWatching: z.boolean(),
  watchers: z.array(WatcherSchema),
});
export type WatchState = z.infer<typeof WatchStateSchema>;
